import styled from "@emotion/styled"
import { PFlyout, PHeading } from "@porsche-design-system/components-react"
import { breakpoint, durationSm } from "@porsche-design-system/components-react/emotion"
import { useCallback, useEffect, useRef, useState } from "react"
import { FormProvider } from "react-hook-form"
import { defineMessages, FormattedMessage } from "react-intl"
import { FeedbackButton, FeedbackForm, FeedbackFormThanks } from "src/components"
import { trackEvent } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useFeedback } from "./useFeedback"

const messages = defineMessages({
	heading: {
		defaultMessage: "Feedback",
		id: "feedbackForm.heading",
	},
})

// pds flyout reference: https://github.com/porsche-design-system/porsche-design-system/blob/4ee9bffe67fd355cd24ce0a4d72b3bb616368ab1/packages/components/src/styles/dialog-styles.ts#L156
const closingDuration = Number.parseFloat(durationSm) * 1000

export const FeedbackFlyoutContainer = withErrorHandler(() => {
	// in order to render this conditionally with nice transition, we need 2 separate states
	// for opening scenario, a small delay is needed in order to render it first, then change apply the prop
	// for closing scenario, we wait for the closing transition to complete
	const [isOpen, setIsOpen] = useState(false)
	const [isOpenWrapped, setIsOpenWrapped] = useState(false)

	const { form, isSubmitted, onReset, onSubmit } = useFeedback()
	const flyoutRef = useRef<HTMLElement & { componentOnReady: () => Promise<void> }>(null)

	useEffect(() => {
		if (isOpenWrapped) {
			onReset()

			// this might be the 1st flyout rendered for the user, therefore he does not have
			// the flyout chunk from pds CDN cached already, so to prevent it from suddenly appearing,
			// we wait for its initialization and open it afterward
			flyoutRef.current!.componentOnReady().then(() => setIsOpen(true))

			trackEvent({
				action: "feedback_flyout_open",
				category: "dealer_feedback",
			})
		}
	}, [isOpenWrapped, onReset])

	useEffect(() => {
		if (!isOpen && isOpenWrapped) {
			setTimeout(() => setIsOpenWrapped(false), closingDuration)

			trackEvent({
				action: "feedback_flyout_close",
				category: "dealer_feedback",
			})
		}
	}, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

	const onFeedbackClick = useCallback(() => setIsOpenWrapped(true), [])
	const onDismiss = useCallback(() => setIsOpen(false), [])

	return (
		<>
			<FeedbackButton onClick={onFeedbackClick} />

			{isOpenWrapped && (
				<FormProvider {...form}>
					<StyledFlyout ref={flyoutRef} disableBackdropClick open={isOpen} onDismiss={onDismiss}>
						<PHeading slot="header">
							<FormattedMessage {...messages.heading} />
						</PHeading>

						{!isSubmitted ? (
							<form onSubmit={form.handleSubmit(onSubmit)} noValidate>
								<FeedbackForm error={<ErrorNotificationInline />} onClose={onDismiss} />
							</form>
						) : (
							<FeedbackFormThanks onClose={onDismiss} />
						)}
					</StyledFlyout>
				</FormProvider>
			)}
		</>
	)
})

const StyledFlyout = styled(PFlyout)`
	--p-flyout-width: 100vw;

	@media (min-width: ${breakpoint.s}px) {
		--p-flyout-width: 680px;
	}
`
